import { xmlStringToBoolean } from '../../../utility/xmlHelpers';
import { xmlToString } from '../../../xmlUtility';
import {
    DESCRIPTION_DETECTNAME,
    DESCRIPTION_SQL_DSNAME,
} from '../constants/parameterDescriptions';
import {
    paramDetectName,
    paramDsName,
    paramParametersAllowNullPath,
    paramParametersDsName,
    paramParametersNamePath,
    paramParametersTypePath,
    paramParametersValuePath,
    paramParametersXpath,
    paramSql,
} from '../constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeXPath,
} from '../constants/parameterTypes';
import { failure, success } from '../helpers/commandResult';
import {
    getBoolParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';
import { getDataConnection } from '../helpers/qRules.dataConnection.helpers';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import { getValidNode, getValidNodeSet } from '../helpers/qRules.node.helpers';

// For now, just using a constant for this because it's used in more places than the other data types
const DATA_TYPE_STRING = 'string';

const SUPPORTED_DATA_TYPES = [
    'boolean',
    'datetime',
    'decimal',
    'integer',
    DATA_TYPE_STRING,
    'innerxml',
];

const SUPPORTED_DATA_TYPES_DELIMITED = SUPPORTED_DATA_TYPES.join(', ');

const optionalParameters = [
    {
        name: paramDetectName,
        description: DESCRIPTION_DETECTNAME,
        type: typeBoolean,
    },
    {
        name: paramParametersDsName,
        description: `(defaults to main data source) The name of the data source in which to evaluate /${paramParametersXpath}`,
        type: typeDataSource,
    },
    {
        name: paramParametersXpath,
        description:
            'An XPath to one or more XML nodes corresponding to parameters to be passed with the SQL query',
        type: typeXPath,
        dsParentName: paramParametersDsName,
    },
    {
        name: paramParametersNamePath,
        description: `A path, relative to /${paramParametersXpath}, indicating where to locate the parameter name for each respective parameter. Required if /${paramParametersXpath} is specified.`,
        type: typeXPath,
        dsParentName: paramParametersDsName,
        relativeTo: paramParametersXpath,
    },
    {
        name: paramParametersValuePath,
        description: `A path, relative to /${paramParametersXpath}, indicating where to locate the paramter value for each respective parameter. Defaults to current parameter node.`,
        type: typeXPath,
        dsParentName: paramParametersDsName,
        relativeTo: paramParametersXpath,
    },
    {
        name: paramParametersTypePath,
        description: `A path, relative to /${paramParametersXpath}, indicating where to locate the data type for each respective parameter. Possible values for the data type are: ${SUPPORTED_DATA_TYPES_DELIMITED}. Defaults to ${DATA_TYPE_STRING} if data type is unspecified.`,
        type: typeXPath,
        dsParentName: paramParametersDsName,
        relativeTo: paramParametersXpath,
    },
    {
        name: paramParametersAllowNullPath,
        description: `A path, relative to /${paramParametersXpath}, to a field that indicates the "allow null" value for each respective parameter. If unspecfied, or the field is not found, "allow null" defaults to false.`,
        type: typeXPath,
        dsParentName: paramParametersDsName,
        relativeTo: paramParametersXpath,
    },
];
const requiredParameters = [
    {
        name: paramDsName,
        description: DESCRIPTION_SQL_DSNAME,
        type: typeDataSource,
    },
    {
        name: paramSql,
        description:
            'SQL to execute. Example: EXEC AdventureWorks.dbo.GetCustomer @customerId = 12345',
    },
];

const executeSqlQueryUnsupportedConnectionTypeError =
    'ExecuteSqlQuery must be used with an SQL data connection.';
const executeSqlQueryError = 'ExecuteSqlQuery error: {0}';
const description =
    'Uses an SQL Data connection to execute a given SQL command.';

// (DataSourceNode, String) -> String
function getParameterType(node, paramsTypePath) {
    if (!paramsTypePath) {
        return DATA_TYPE_STRING;
    }

    const type = node.evaluateString(paramsTypePath);

    if (!type) {
        return DATA_TYPE_STRING;
    }

    const typeNormal = type.toLowerCase().trim();

    const isValid = SUPPORTED_DATA_TYPES.includes(typeNormal);

    if (isValid) {
        return typeNormal;
    }

    throw new Error(
        `Incorrect data type value specified for /${paramParametersTypePath}: ${type}. Supported types are: ${SUPPORTED_DATA_TYPES_DELIMITED}`,
    );
}

function getParameterValue(node, paramsValuePath, paramType) {
    const parameterNode = getValidNode(
        node,
        paramsValuePath,
        `Could not find query parameter value at XPath ${paramsValuePath}`,
    );

    if (paramType !== 'innerxml') {
        return parameterNode.value();
    }

    return parameterNode
        .selectNodes('node()')
        .map((childNode) => xmlToString(childNode.getNode()))
        .join('');
}

function getParameterAllowNull(node, paramsAllowNullPath) {
    if (!paramsAllowNullPath) {
        return false;
    }

    const allowNullNode = node.selectSingle(paramsAllowNullPath);

    return allowNullNode && allowNullNode.value()
        ? xmlStringToBoolean(allowNullNode.value())
        : false;
}

function createParameterItem(
    node,
    paramsNamePath,
    paramsValuePath,
    paramsTypePath,
    paramsAllowNullPath,
) {
    const paramName = node.evaluateString(paramsNamePath);

    if (!paramName) {
        throw new Error(
            `Could not find query parameter name at XPath ${paramsNamePath}`,
        );
    }

    const allowNull = getParameterAllowNull(node, paramsAllowNullPath);
    const paramType = getParameterType(node, paramsTypePath);

    return {
        allowNull,
        name: paramName,
        value: getParameterValue(node, paramsValuePath, paramType),
        type: paramType,
    };
}

function getSqlParameters(parameters, dataSources) {
    const paramsXpath = getParamValue(parameters, paramParametersXpath);
    const paramsDsName = getParamValue(parameters, paramParametersDsName);
    const paramsNamePath = getParamValue(parameters, paramParametersNamePath);
    const paramsValuePath = getParamValue(
        parameters,
        paramParametersValuePath,
        '.',
    );
    const paramsTypePath = getParamValue(parameters, paramParametersTypePath);
    const paramsAllowNullPath = getParamValue(
        parameters,
        paramParametersAllowNullPath,
    );

    if (!paramsXpath) {
        return [];
    }

    if (!paramsNamePath) {
        throw new Error(
            `/${paramParametersNamePath} is required if /${paramParametersXpath} is specified.`,
        );
    }

    const paramsDs = getDataSource(dataSources, paramsDsName);
    const paramsNodes = getValidNodeSet(
        paramsDs,
        paramsXpath,
        `Failed to select any parameters at ${paramsXpath}`,
    );

    return paramsNodes.map((node) =>
        createParameterItem(
            node,
            paramsNamePath,
            paramsValuePath,
            paramsTypePath,
            paramsAllowNullPath,
        ),
    );
}

const executeSqlAsync = async (
    commandParameters,
    dataSources,
    dc,
    sql,
    detectName,
) => {
    const parameters = getSqlParameters(commandParameters, dataSources);
    const origCmdText = dc.getCommandText();

    const execOptions = {
        detectName,
        parameters,
    };

    try {
        dc.setCommandText(sql);

        await dc.queryAsync(execOptions);

        return success();
    } catch (e) {
        return failure(String.format(executeSqlQueryError, e));
    } finally {
        dc.setCommandText(origCmdText);
    }
};

function executeAsync({ dataConnections, dataSources, parameters }) {
    const dsName = getParamValue(parameters, paramDsName);
    const sql = getParamValue(parameters, paramSql);
    const detectName = getBoolParamValue(parameters, paramDetectName, false);
    const dc = getDataConnection(dataConnections, dsName);

    if (!dc.isAdoSupported()) {
        return failure(executeSqlQueryUnsupportedConnectionTypeError);
    }

    return executeSqlAsync(parameters, dataSources, dc, sql, detectName);
}

const ExecuteSqlQuery = {
    description,
    executeAsync,
    optionalParameters,
    requiredParameters,
};

export default ExecuteSqlQuery;
